import {
  Box,
  Collapse,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import Card from "../../common/Card";
import Table from "../../common/CustomTable";
import {
  SearchOutlined,
  PictureAsPdfOutlined,
  DataObjectOutlined,
  MapOutlined,
  TrackChanges,
} from "@mui/icons-material";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import { PharmacyCoverageNavState } from "../pharmacyCoverage/PharmacyShipToCoverage";
import Button from "../../common/Button";
import { useLocation } from "react-router-dom";
import CustomTextField from "../../common/CustomTextField";
import { useForm } from "react-hook-form";
import CustomMultipleDropDown from "../../common/CustomMultipleDropDown";
import CustomDateRange from "../../common/CustomDateRange";
import CustomIconButton from "../../common/CustomIconButton";
import PDFViewer from "../../common/pdf/PDFViewer";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { selectIsValueFoundInToken, useAuthSlice } from "../../../redux/slice/authSlice";
import {
  fetchPrescriptionList,
  fetchPracticesByUserRolePrescription,
  resetPrescriptionListInfo,
  usePrescriptionSlice,
  SearchPrescriptionRequest,
  PrescriptionListItem,
  RxOpenAccessProduct,
  submitPrescription,
  fetchGenesisIntakeRecord,
} from "../../../redux/slice/prescription/prescriptionSlice";
import {
  AUDIT_ENTITYS,
  CAN_ACCESS_APPLICATION,
  PRESCRIPTION_ACTIONS,
  USER_ROLE_ENTITY_NAMES,
  USER_ROLES,
} from "../../../constants/applicationConstants";
import { fetchPharmaciesList, useCommonReducer } from "../../../redux/slice/commonSlice";
import { formatDate, sortNames } from "../../../utils/Utility";
import { createListSort } from "../../../utils/listSort";
import {
  getSearchCriteriaFromSessionStorage,
  setSearchCriteriaToSessionStorage,
} from "../../../utils/StorageUtils";
import { tokens } from "../../../theme";
import { DateObject } from "react-multi-date-picker";
import { setSingleAlertObj, showHideLoadingDialog } from "../../../redux/slice/commonSlice";
import { useNavigation } from "../../../hooks/useNavigation";
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";
import { fetchOrderDocumentById } from "../../../redux/slice/order/OrderSlice";
import CustomJsonPopUp from "../../common/CustomJsonPopUp";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { fetchActivePracticeProviders, usePracticeProvidersSlice } from "../../../redux/slice/practice/practiceProvidersSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import RxProductSelectionPopup from "./RxProductSelectionPopup";
import PrescriptionModalPopup from "./PrescriptionModalPopup";
import { MarketProduct } from "../../../redux/slice/exploreMarket/exploreMarketSlice";
import { useModalStyles } from "../../common/useModalStyles";

const RX_REQUEST_STATUS_OPTIONS = [
  { label: "PENDING", value: "PENDING" },
  { label: "SUBMITTED", value: "SUBMITTED" },
  { label: "ON_HOLD", value: "ON_HOLD" },
  { label: "POSTED_TO_PHARMACY", value: "POSTED_TO_PHARMACY" },
  { label: "IN_FULFILLMENT", value: "IN_FULFILLMENT" },
  { label: "SHIPPED", value: "SHIPPED" },
  { label: "DELIVERED", value: "DELIVERED" },
  { label: "CANCELLED", value: "CANCELLED" },
  { label: "FAILED", value: "FAILED" },
  { label: "BLOCKED", value: "BLOCKED" },
];

const DEFAULT_RX_REQUEST_STATUSES = RX_REQUEST_STATUS_OPTIONS
  .map((option) => option.value)
  .filter((value) => value !== "BLOCKED");

const rxRequestStatuses = (status?: string[] | null) =>
  status == null ? DEFAULT_RX_REQUEST_STATUSES : status;

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
}

interface Row {
  prescriptionNumber: string | JSX.Element;
  patientName: string;
  providerName: string;
  practiceName: string;
  productName: string;
  createdAt: string;
  action: JSX.Element | null;
}

type Option = {
  value: string;
  label: string;
};

const ITEMS_PER_PAGE = 20;

const prescriptionListSort = createListSort(
  ["patientName", "providerName", "practiceName", "manufacturerName", "productName", "status", "createdAt"] as const,
  {
    patientName: "patientName",
    providerName: "providerName",
    practiceName: "practiceName",
    manufacturerName: "manufacturerName",
    productName: "productName",
    rxStatus: "status",
    createdAt: "createdAt",
  }
);

function isGenesisPharmacy(row: PrescriptionListItem): boolean {
  return (row.manufacturerName || "").toLowerCase().includes("genesis");
}

const PrescriptionList = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { primaryBtnSx, tokens: modalTokens } = useModalStyles();

  const { control, handleSubmit, setValue, watch } =
    useForm<SearchPrescriptionRequest>({
      defaultValues: { status: DEFAULT_RX_REQUEST_STATUSES },
    });

  const [showCard, setShowCard] = useState(false);
  const {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  } = useFilterPanelFocus(showCard, setShowCard);
  const [isDateRangeChanged, setIsDateRangeChanged] = useState(false);
  const [isClearDateRange, setIsClearDateRange] = useState(false);
  const [showPDF, setShowPDF] = useState<{
    isShow: boolean;
    title: string;
    fileName: string;
  }>({ isShow: false, title: "", fileName: "" });
  const [pdfContent, setPdfContent] = useState<string>("");
  const [jsonPopup, setJsonPopup] = useState<{
    isShow: boolean;
    title: string;
    data: any;
  }>({ isShow: false, title: "", data: null });
  const [showProductSelectionPopup, setShowProductSelectionPopup] = useState(false);
  const [selectedProductForRx, setSelectedProductForRx] = useState<MarketProduct | null>(null);
  const [initialPracticeIdForRx, setInitialPracticeIdForRx] = useState<number | undefined>();
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);

  const [searchCriteria, setSearchCriteria] =
    useState<SearchPrescriptionRequest>({
      practiceIds: [],
      patientFirstName: "",
      patientLastName: "",
      providerIds: [],
      prescriptionNumber: "",
      fromDate: "",
      toDate: "",
      status: DEFAULT_RX_REQUEST_STATUSES,
      manufacturerId: null,
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
    });

  const { userInfo } = useAuthSlice();
  const { pharmacyInfoList, fetchPharmacyListStatus } = useCommonReducer();
  const {
    prescriptionListInfo,
    practicesByUserRole,
    practicesFetchStatus,
  } = usePrescriptionSlice();
  const { practiceProvidersInfo } = usePracticeProvidersSlice();
  const watchedPracticeIds = watch("practiceIds");

  const canViewPrescription = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessPrescriptionActions", PRESCRIPTION_ACTIONS.VIEW_PDF)(state));
  const canViewResponseJSON = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessPrescriptionActions", PRESCRIPTION_ACTIONS.VIEW_RESPONSE_JSON)(state));
  const canAuditPrescription = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessAuditAction", AUDIT_ENTITYS.PRESCRIPTIONAUDIT)(state));

  const autoSelectedPharmacyRef = useRef<number | null>(null);
  // Prevents a second default search if practices are refetched after first paint.
  const didLoadInitialListRef = useRef(false);
  const lastSearchedManufacturerIdRef = useRef<number | null | undefined>(undefined);
  const pharmaciesResolved =
    fetchPharmacyListStatus === "success" || fetchPharmacyListStatus === "error";

  const isAdminRole =
    userInfo?.userRole === USER_ROLES.ROLE_SUPERADMIN ||
    userInfo?.userRole === USER_ROLES.ROLE_ADMIN;

  const resolveDefaultPracticeIds = (): number[] => {
    // Admin and Super Admin search all practices unless they pick one.
    if (isAdminRole) {
      return [];
    }

    const fromPractices = (practicesByUserRole ?? [])
      .map((p: any) => Number(p.practiceId))
      .filter((id: number) => Number.isFinite(id) && id > 0);
    if (fromPractices.length === 1) {
      return fromPractices;
    }

    // Practice Admin / practice-entity users have their practice on the token
    // immediately, so the first search does not wait on getPracticesByUserRole.
    const fromEntity = (userInfo?.entityIds ?? [])
      .map((id: number) => Number(id))
      .filter((id: number) => Number.isFinite(id) && id > 0);
    if (
      userInfo?.entityName === USER_ROLE_ENTITY_NAMES.PRACTICE &&
      fromEntity.length > 0
    ) {
      return fromEntity;
    }

    return [];
  };

  const practicesReady =
    resolveDefaultPracticeIds().length > 0 ||
    (practicesByUserRole && practicesByUserRole.length > 0) ||
    practicesFetchStatus === "success" ||
    practicesFetchStatus === "error";

  // Mount: fetch initial data
  useEffect(() => {
    fetchInitialData();
    return () => {
      dispatch(resetPrescriptionListInfo());
    };
  }, []);

  // Load list on mount or location state change
  useEffect(() => {
    const criteria = getSearchCriteriaFromSessionStorage(
      CAN_ACCESS_APPLICATION.PRESCRIPTION_MANAGEMENT +
      "SearchCriteria" +
      userInfo.userId
    );
    if (
      location.state &&
      location.state.prescriptionListSearchCriteria
    ) {
      const sc: SearchPrescriptionRequest = {
        ...location.state.prescriptionListSearchCriteria,
        status: rxRequestStatuses(location.state.prescriptionListSearchCriteria.status),
      };
      if (!sc.practiceIds || sc.practiceIds.length === 0) {
        const lockedPracticeIds = resolveDefaultPracticeIds();
        if (lockedPracticeIds.length > 0) {
          sc.practiceIds = lockedPracticeIds;
        }
      }
      setSearchCriteria(sc);
      setValuesToCriteriaFields(sc);
      searchPrescriptionsList(sc);
    } else if (location.state?.withSearchHistory && criteria) {
      navigate("/prescription_list", {
        state: { prescriptionListSearchCriteria: criteria },
      });
    } else if (
      pharmaciesResolved &&
      practicesReady &&
      !didLoadInitialListRef.current
    ) {
      // Wait for pharmacies (and practices, unless the role already has entityIds)
      // so the first search includes the locked practice and default pharmacy.
      didLoadInitialListRef.current = true;
      loadInitialList(1);
    }
  }, [location.state, practicesByUserRole, pharmaciesResolved, practicesFetchStatus]);

  const fetchInitialData = async () => {
    dispatch(showHideLoadingDialog(true));
    await dispatch(
      fetchPracticesByUserRolePrescription({
        isActivePracticeOnly: true,
        skipLoader: true,
      })
    );
    await dispatch(fetchPharmaciesList({ skipLoader: true }));
    dispatch(showHideLoadingDialog(false));
  };

  // CS-2075 — pharmacy filter options from getPharmacies.
  const pharmacyOptions = useMemo(() => {
    let options: { label: string; value: number }[] = [];
    (pharmacyInfoList ?? []).forEach((pharmacy) => {
      options.push({
        label: pharmacy.manufacturerName,
        value: pharmacy.manufacturerId,
      });
    });
    return options;
  }, [pharmacyInfoList]);

  // Only a lone pharmacy is defaulted; with none or several the filter stays empty.
  const defaultPharmacyId = useMemo(
    () => (pharmacyOptions.length === 1 ? Number(pharmacyOptions[0].value) : null),
    [pharmacyOptions]
  );

  // Fetch providers whenever the list of practices changes
  useEffect(() => {
    const sorted = sortNames(practicesByUserRole ?? [], "practiceName");
    const practiceIds = sorted.map((p: any) => Number(p.practiceId));
    if (practiceIds.length > 0) {
      const data = { practiceIds, onlyActiveRecords: false };
      dispatch(fetchActivePracticeProviders({ data, skipLoader: true }));
    }
  }, [practicesByUserRole]);

  const practiceOptions = useMemo(() => {
    let options: Option[] = [];
    if (practicesByUserRole && practicesByUserRole.length > 0) {
      if (practicesByUserRole.length === 1 && !isAdminRole) {
        const practiceVal = [Number(practicesByUserRole[0]?.practiceId)];
        setValue("practiceIds", practiceVal);
      }
      const sorted = sortNames(practicesByUserRole, "practiceName");
      sorted?.forEach((practice: any) => {
        options.push({
          value: practice.practiceId,
          label: practice.practiceName,
        });
      });
    }
    return options;
  }, [practicesByUserRole]);

  const practiceProviderList = useMemo(() => {
    let options: { label: string; value: any }[] = [];

    if (!Array.isArray(practiceProvidersInfo)) return options;

    const filteredProviders =
      watchedPracticeIds?.length > 0
        ? practiceProvidersInfo.filter((provider) =>
          watchedPracticeIds.includes(Number(provider.practiceId))
        )
        : practiceProvidersInfo;

    // If ROLE_PROVIDER, restrict to that specific provider
    if (userInfo?.entityName === USER_ROLE_ENTITY_NAMES.PROVIDER) {
      const matchedProvider = filteredProviders.find(
        (provider) =>
          provider.firstName === userInfo.firstName &&
          provider.lastName === userInfo.lastName
      );

      if (matchedProvider) {
        options.push({
          label: `${matchedProvider.title ? matchedProvider.title + " " : ""}${matchedProvider.firstName} ${matchedProvider.middleName ? matchedProvider.middleName + " " : ""
            }${matchedProvider.lastName}`,
          value: matchedProvider.providerId,
        });
        setValue("providerIds", options[0].value);
      }
    } else {
      // Normal list for non-provider roles
      filteredProviders.forEach((item) => {
        options.push({
          label: `${item.title ? item.title + " " : ""}${item.firstName} ${item.middleName ? item.middleName + " " : ""
            }${item.lastName}`,
          value: item.providerId,
        });
      });
    }

    return options;
  }, [practiceProvidersInfo, watchedPracticeIds, userInfo]);

  const columns: Column[] = useMemo(() => {
    return [
      { id: "patientName", label: "Patient", minWidth: 120 },
      { id: "providerName", label: "Provider", minWidth: 120 },
      { id: "practiceName", label: "Practice", minWidth: 120 },
      { id: "manufacturerName", label: "Pharmacy", minWidth: 120 },
      { id: "productName", label: "Product", minWidth: 120 },
      { id: "rxStatus", label: "Status", minWidth: 100 },
      { id: "createdAt", label: "Created Date", minWidth: 110 },
      { id: "action", label: "Action", minWidth: 50 },
    ];
  }, []);

  const loadInitialList = (pageNumber: number = 1) => {
    const data: SearchPrescriptionRequest = {
      practiceIds: resolveDefaultPracticeIds(),
      patientFirstName: "",
      patientLastName: "",
      providerIds: [],
      prescriptionNumber: "",
      fromDate: "",
      toDate: "",
      status: DEFAULT_RX_REQUEST_STATUSES,
      manufacturerId: defaultPharmacyId,
      currentPage: pageNumber,
      itemsPerPage: ITEMS_PER_PAGE,
    };
    setSearchCriteria(data);
    setValuesToCriteriaFields(data);
    searchPrescriptionsList(data);
  };

  const searchPrescriptionsList = (criteria: SearchPrescriptionRequest) => {
    const normalizedCriteria = prescriptionListSort.normalize(criteria);
    let dataToSearch = { ...normalizedCriteria };
    if (!dataToSearch.practiceIds || dataToSearch.practiceIds.length === 0) {
      const lockedPracticeIds = resolveDefaultPracticeIds();
      if (lockedPracticeIds.length > 0) {
        dataToSearch.practiceIds = lockedPracticeIds;
      }
    }

    // Single pharmacy must be sent as manufacturerId, same as the default dropdown value.
    if (dataToSearch.manufacturerId == null && defaultPharmacyId != null) {
      dataToSearch.manufacturerId = defaultPharmacyId;
    }
    lastSearchedManufacturerIdRef.current = dataToSearch.manufacturerId ?? null;

    setSearchCriteriaToSessionStorage(
      CAN_ACCESS_APPLICATION.PRESCRIPTION_MANAGEMENT +
      "SearchCriteria" +
      userInfo.userId,
      dataToSearch
    );

    dispatch(fetchPrescriptionList({ data: dataToSearch, skipLoader: false }));
  };

  const applyPharmacyFilter = (manufacturerId: number | null) => {
    const nextCriteria: SearchPrescriptionRequest = {
      ...searchCriteria,
      manufacturerId,
    };
    setSearchCriteria(nextCriteria);
    setValuesToCriteriaFields(nextCriteria);
    searchPrescriptionsList(nextCriteria);
  };

  // Same as Request Rx: a single pharmacy is selected by default and sent on search.
  // With several pharmacies nothing is pre-selected and manufacturerId stays null.
  useEffect(() => {
    if (defaultPharmacyId !== null) {
      autoSelectedPharmacyRef.current = defaultPharmacyId;
      setValue("manufacturerId", defaultPharmacyId);
      if (
        Number(searchCriteria.manufacturerId) === defaultPharmacyId ||
        Number(lastSearchedManufacturerIdRef.current) === defaultPharmacyId
      ) {
        return;
      }
      applyPharmacyFilter(defaultPharmacyId);
      return;
    }

    // The list grew beyond one pharmacy, so drop the selection we defaulted earlier.
    if (autoSelectedPharmacyRef.current === null) return;
    autoSelectedPharmacyRef.current = null;
    setValue("manufacturerId", null);
    if (searchCriteria.manufacturerId == null) return;
    applyPharmacyFilter(null);
  }, [defaultPharmacyId, searchCriteria.manufacturerId, setValue]);

  const setValuesToCriteriaFields = (criteria: SearchPrescriptionRequest) => {
    setValue("practiceIds", criteria.practiceIds);
    setValue("patientFirstName", criteria.patientFirstName);
    setValue("patientLastName", criteria.patientLastName);
    setValue("providerIds", criteria.providerIds);
    setValue("prescriptionNumber", criteria.prescriptionNumber);
    setValue("status", criteria.status);
    setValue("manufacturerId", criteria.manufacturerId ?? null);

    if (criteria.fromDate && criteria.toDate) {
      setValue(
        "dateRange" as any,
        `${criteria.fromDate} - ${criteria.toDate}`
      );
    } else if (criteria.fromDate) {
      setValue("dateRange" as any, criteria.fromDate);
    }
  };

  const onSubmit = (data: any) => {
    if (!isDateRangeChanged) {
      setValue("fromDate" as any, "");
      setValue("toDate" as any, "");
      setValue("dateRange" as any, "");
      setIsClearDateRange(true);
    }
    const criteria: SearchPrescriptionRequest = {
      practiceIds: data.practiceIds || [],
      patientFirstName: data.patientFirstName || "",
      patientLastName: data.patientLastName || "",
      providerIds: data.providerIds || "",
      prescriptionNumber: data.prescriptionNumber || "",
      fromDate: isDateRangeChanged ? (data.fromDate || "") : "",
      toDate: isDateRangeChanged ? (data.toDate || "") : "",
      status: rxRequestStatuses(data.status),
      manufacturerId: data.manufacturerId ? Number(data.manufacturerId) : null,
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
      ...prescriptionListSort.pick(searchCriteria),
    };
    setSearchCriteria(criteria);
    navigate("/prescription_list", {
      state: { prescriptionListSearchCriteria: criteria },
    });
  };

  const handlePagination = (pageNumber: number) => {
    const data = {
      ...searchCriteria,
      currentPage: pageNumber + 1,
    };
    setSearchCriteria(data);
    navigate("/prescription_list", {
      state: { prescriptionListSearchCriteria: data },
    });
  };

  const handleTableSort = (columnId: string, order: "asc" | "desc") => {
    const data = prescriptionListSort.withSort(searchCriteria, columnId, order);
    if (!data) {
      return;
    }
    setSearchCriteria(data);
    navigate("/prescription_list", {
      state: { prescriptionListSearchCriteria: data },
    });
  };

  const handleDateRangeChange = (
    fromDate: DateObject | null,
    toDate: DateObject | null
  ) => {
    const fromDateString = fromDate ? fromDate.format("MM/DD/YYYY") : null;
    const toDateString = toDate ? toDate.format("MM/DD/YYYY") : null;
    setValue("fromDate" as any, fromDateString || "");
    setValue("toDate" as any, toDateString || "");
    setSearchCriteria((prev) => ({
      ...prev,
      fromDate: fromDateString || "",
      toDate: toDateString || "",
    }));
    setIsDateRangeChanged(true);
  };

  const handlePreviewPDF = async (row: PrescriptionListItem) => {
    if (!row.documentId) return;
    const actionResult = await dispatch(fetchOrderDocumentById(row.documentId));
    const docData = actionResult.payload;
    if (docData?.content) {
      setPdfContent(docData.content);
      setShowPDF({
        isShow: true,
        title: `RX - ${row.patientName} - ${row.productName}`,
        fileName: row.patientName,
      });
    }
  };

  // Only BLOCKED is highlighted (red); all other statuses use default table text color.
  const getStatusColor = (status: string): string | undefined => {
    if (status?.toUpperCase() === "BLOCKED") {
      return "#ef4444";
    }
    return undefined;
  };

  const handleViewJson = async (row: PrescriptionListItem) => {
    if (isGenesisPharmacy(row)) {
      const actionResult = await dispatch(
        fetchGenesisIntakeRecord({ prescriptionId: row.prescriptionId })
      );
      if (fetchGenesisIntakeRecord.fulfilled.match(actionResult) && actionResult.payload) {
        setJsonPopup({
          isShow: true,
          title: `Response Payload - ${row.patientName || "RX-" + row.prescriptionId}`,
          data: actionResult.payload,
        });
      }
      return;
    }
    if (!row.responsePayload) return;
    let parsed: any;
    try {
      parsed = JSON.parse(row.responsePayload);
    } catch {
      parsed = row.responsePayload;
    }
    setJsonPopup({
      isShow: true,
      title: `Response Payload - ${row.patientName || "RX-" + row.integrationPrescriptionId}`,
      data: parsed,
    });
  };

  // CS-2073 — open Audit Log filtered to this Rx request (same pattern as IVR list audit)
  const handleClickPrescriptionAudit = (row: PrescriptionListItem) => {
    const prescriptionId = row?.prescriptionId;
    if (!prescriptionId) return;
    // Audit rows are keyed by local prescription.id (see logRxGatingBlockedAttempt / submit)
    const url = `/auditlog?entityName=PRESCRIPTION&entityNumber=${encodeURIComponent(String(prescriptionId))}`;
    window.open(url, "_blank");
  };

  const actionComponent = (row: PrescriptionListItem): JSX.Element | null => {
    // CS-2075 START — return null when no action applies (e.g. BLOCKED rows) so the table hides the empty menu
    const showJson = canViewResponseJSON && (isGenesisPharmacy(row) || !!row.responsePayload);
    const showPdf = canViewPrescription && !!row.documentId;
    const showAudit = canAuditPrescription && row.prescriptionId != null;
    if (!showJson && !showPdf && !showAudit) return null;
    // CS-2075 END
    return (
      <>
        {canViewResponseJSON && (isGenesisPharmacy(row) || row.responsePayload) && (
          <CustomIconButton
            color="success"
            size="small"
            variant="outlined"
            onClick={() => handleViewJson(row)}
            tooltipMessage="Response JSON"
            tooltipPlacement="top"
          >
            <DataObjectOutlined fontSize="small" />
          </CustomIconButton>
        )}
        {canViewPrescription && row.documentId && (
          <CustomIconButton
            color="info"
            size="small"
            variant="outlined"
            onClick={() => handlePreviewPDF(row)}
            tooltipMessage="Rx"
            tooltipPlacement="top"
          >
            <PictureAsPdfOutlined fontSize="small" />
          </CustomIconButton>
        )}
        {canAuditPrescription && row.prescriptionId != null && (
          <CustomIconButton
            color="warning"
            size="small"
            variant="outlined"
            onClick={() => handleClickPrescriptionAudit(row)}
            tooltipMessage="Rx Audit"
            tooltipPlacement="top"
          >
            <TrackChanges fontSize="small" />
          </CustomIconButton>
        )}
      </>
    );
  };

  const handleRxProductSelect = (
    product: RxOpenAccessProduct,
    context?: { practiceId?: number }
  ) => {
    setShowProductSelectionPopup(false);
    setInitialPracticeIdForRx(context?.practiceId);
    const marketProduct: MarketProduct = {
      productId: product.productId,
      productName: product.productName,
      hcpcsCode: product.hcpcsCode || null,
      productCategory: product.productCategory || null,
      composition: product.composition || null,
      allowableRate: null,
      listPrice: null,
      mues: null,
      manufacturerId: product.manufacturerId ?? null,
      manufacturerName: product.manufacturerName || null,
      isAgreementRequired: null,
      agreementStatus: null,
      agreementMode: null,
      productLogoId: null,
      isFavorited: false,
      productStatus: null,
      extendedPrice:
        product.extendedPrice != null && product.extendedPrice !== ""
          ? Number(product.extendedPrice)
          : null,
    };
    setSelectedProductForRx(marketProduct);
    setShowPrescriptionModal(true);
  };

  const handleSubmitPrescription = async (payload: any): Promise<any[] | null> => {
    if (payload) {
      const result = await dispatch(submitPrescription({ request: payload, skipLoader: false }));
      const body = result?.payload;
      const rows = Array.isArray(body?.data)
        ? body.data
        : Array.isArray(body)
          ? body
          : null;
      if (!rows?.length) {
        dispatch(setSingleAlertObj({
          message: body?.message || "An error occurred while submitting the Rx Request.",
          alertType: "error",
        }));
      }
      return rows;
    }
    return null;
  };

  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const listPanelBg = isDark ? "#141720" : "rgba(255,255,255,0.82)";
  const listHoverBg = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";

  const listCardSx = {
    borderRadius: "14px",
    border: `1px solid ${listBorderColor}`,
    backgroundColor: listPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
  };

  const filterPanelSx = {
    ...listCardSx,
    p: { xs: "14px !important", md: "18px !important" },
    position: "relative",
    zIndex: 20,
    overflow: "visible",
    "& .MuiAutocomplete-root, & .MuiFormControl-root": {
      width: "100%",
    },
    "& .MuiTextField-root": {
      height: "42px",
      minHeight: "42px !important",
    },
    "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root": {
      minHeight: "42px !important",
      height: "42px !important",
      borderRadius: "10px !important",
      backgroundColor: `${modalTokens.inputBg} !important`,
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      alignItems: "center",
      boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
      padding: "0 38px 0 12px !important",
    },
    "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input": {
      paddingTop: "8px !important",
      paddingBottom: "8px !important",
      height: "24px !important",
      lineHeight: "24px !important",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input": {
      paddingLeft: "12px !important",
      paddingRight: "12px !important",
    },
    "& .MuiAutocomplete-endAdornment": {
      top: "50% !important",
      transform: "translateY(-50%)",
      right: "8px !important",
    },
    "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root fieldset": {
      borderColor: `${modalTokens.inputBorder} !important`,
    },
    "& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: `${modalTokens.accent} !important`,
      borderWidth: "1px !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)",
    },
    "& .MuiInputBase-input": {
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
    },
    "& input::selection": {
      color: modalTokens.textPrimary,
      backgroundColor: isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.2)",
    },
    "& .MuiOutlinedInput-root:has(input:-webkit-autofill)": {
      backgroundColor: "#376c9f !important",
      "& fieldset": {
        borderColor: "#7fb1df !important",
      },
    },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
      WebkitTextFillColor: "#ffffff",
      caretColor: "#ffffff",
      WebkitBoxShadow: "0 0 0 100px #376c9f inset",
      borderRadius: "10px",
      transition: "background-color 5000s ease-in-out 0s",
    },
    "& .MuiInputLabel-root": { color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", fontSize: "0.88rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: `${modalTokens.accent} !important` },
    "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 9px) scale(1)" },
    "& .MuiChip-root": {
      borderRadius: "999px",
      backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.1)",
      color: modalTokens.accent,
      border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(20,184,166,0.18)"}`,
      fontWeight: 600,
    },
    "& .MuiSvgIcon-root": {
      color: modalTokens.textSecondary,
    },
    "& .rmdp-container": {
      width: "100%",
      height: "42px",
      overflow: "visible",
      zIndex: 30,
    },
    "& .rmdp-wrapper, & .rmdp-calendar, & .rmdp-day-picker": {
      height: "auto !important",
      minHeight: "unset !important",
      overflow: "visible",
    },
    "& .rmdp-wrapper": {
      zIndex: "1600 !important",
      position: "absolute",
    },
  };

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": {
      color: "#fff",
      fontWeight: 700,
    },
    "& .MuiButton-startIcon": {
      color: "#fff",
    },
    "& .MuiSvgIcon-root": {
      color: "#fff",
    },
  };

  const filledTealBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#fff",
    backgroundColor: "#00B8D9",
    border: "1px solid #00B8D9",
    whiteSpace: "nowrap",
    "& .MuiTypography-root": { color: "#fff" },
    "& .MuiButton-startIcon": { color: "#fff" },
    "&:hover": {
      backgroundColor: "#0099bb",
      borderColor: "#0099bb",
      boxShadow: "0 6px 22px rgba(0,184,217,0.35)",
    },
  };

  const outlineBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap",
    "&:hover": {
      color: "#fff",
      borderColor: "#00B8D9",
      backgroundColor: "#00B8D9",
      boxShadow: "0 6px 22px rgba(0,184,217,0.28)",
    },
  };

  const tableSx = {
    "& .MuiTableContainer-root": {
      borderRadius: "12px",
      border: "none",
      backgroundColor: "transparent",
    },
    "& table": {
      
      borderCollapse: "collapse",
      borderSpacing: 0,
    },
    "& .MuiTableCell-root": {
      borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
      py: 1.15,
      fontSize: "0.84rem",
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      height: "42px",
    },
    "& .MuiTableHead-root .MuiTypography-root": {
      color: "#00B8D9 !important",
      fontSize: "0.78rem",
      fontWeight: 800,
      letterSpacing: "0.02em",
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: "background-color 0.18s ease, box-shadow 0.18s ease",
      // CS-2075 — rows without actions (e.g. BLOCKED) match the ⋮ row height (40px button + cell padding)
      height: "60px",
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: `${listHoverBg} !important`,
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      color: modalTokens.textPrimary,
      fontSize: "0.84rem",
    },
    // Exclude status labels so per-status palette (sx color) is not overridden
    // (same idea as IVR list `.ivr-status-text`).
    "& .MuiTableBody-root .MuiTypography-root:not(.rx-status-text)": {
      color: modalTokens.textPrimary,
      fontSize: "0.84rem",
    },
    "& .MuiIconButton-root": {
      color: isDark ? "#94a3b8" : "#475569",
      borderRadius: "8px",
      "&:hover": {
        color: modalTokens.accent,
        backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)",
      },
    },
    "& .MuiPaginationItem-root": {
      borderColor: listBorderColor,
      color: modalTokens.textSecondary,
      borderRadius: "8px",
      "&.Mui-selected": {
        color: "#fff",
        backgroundColor: modalTokens.accent,
        borderColor: modalTokens.accent,
      },
    },
  };

  const prescriptionListData = useMemo(() => {
    if (!prescriptionListInfo) return [];
    return prescriptionListInfo.content?.map(
      (details: PrescriptionListItem) => {
        const prescriptionId =
          details.prescriptionId ?? (details as any).id ?? null;
        const statusValue = details.status || "";
        const statusColor = getStatusColor(statusValue);
        return {
        ...details,
        prescriptionId,
        // Keep raw status string for CustomTable status-column logic; display uses rxStatus.
        status: statusValue,
        prescriptionNumber: (
          <Typography variant="body2" sx={{ display: "inline-block" }}>
            {details.integrationPrescriptionId || `RX-${prescriptionId}`}
          </Typography>
        ),
        patientName: details.patientName || "---",
        providerName: details.providerName || "---",
        practiceName: details.practiceName || "---",
        manufacturerName: details.manufacturerName || "---",
        productName: details.productName || "---",
        drugName: details.drugName || "---",
        rxStatus: (
          <Typography
            variant="body2"
            className={statusColor ? "rx-status-text" : undefined}
            sx={{
              display: "inline-block",
              ...(statusColor ? { color: statusColor, fontWeight: 600 } : {}),
            }}
          >
            {statusValue || "---"}
          </Typography>
        ),
        createdAt: formatDate(details.createdAt),
      };
      }
    ) || [];
  }, [prescriptionListInfo]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        px: { xs: 1, md: 3 },
        py: 2,
      }}
    >
      {jsonPopup.isShow && (
        <CustomJsonPopUp
          open={jsonPopup.isShow}
          onClose={() => setJsonPopup({ isShow: false, title: "", data: null })}
          title={jsonPopup.title}
          object={jsonPopup.data}
          alwaysShowContent={true}
        />
      )}

      {showPDF.isShow && (
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() => {
            setShowPDF({ isShow: false, title: "", fileName: "" });
            setPdfContent("");
          }}
          title={showPDF.title}
          fileName={showPDF.fileName}
          base64String={pdfContent}
        />
      )}

      <Box sx={{ position: "relative", zIndex: 20, overflow: "visible" }}>
        <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
          <Card sx={filterPanelSx}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item md={3} xs={12}>
                  <CustomDropdownField
                    name="manufacturerId"
                    label="Pharmacy"
                    placeholderText="Select pharmacy"
                    options={pharmacyOptions}
                    control={control}
                    sortOptions={false}
                    size="small"
                  />
                </Grid>
                <Grid item md={3} xs={12}>
                  <CustomMultipleDropDown
                    name="practiceIds"
                    label="Practice"
                    placeholderText="Select practice"
                    disabled={practicesByUserRole?.length === 1 && !isAdminRole}
                    options={practiceOptions}
                    control={control}
                    size="small"
                  />
                </Grid>
                <Grid item md={3} xs={12}>
                  <CustomTextField
                    control={control}
                    controllerName="patientFirstName"
                    label="Patient First Name"
                    placeholder="Enter first name"
                    size="small"
                  />
                </Grid>
                <Grid item md={3} xs={12}>
                  <CustomTextField
                    control={control}
                    controllerName="patientLastName"
                    label="Patient Last Name"
                    placeholder="Enter last name"
                    size="small"
                  />
                </Grid>
                <Grid item md={3} xs={12}>
                  <CustomMultipleDropDown
                    control={control}
                    name="providerIds"
                    label="Provider"
                    placeholderText="Select provider"
                    options={practiceProviderList}
                    disabled={userInfo?.entityName === USER_ROLE_ENTITY_NAMES.PROVIDER}
                    size="small"
                  />
                </Grid>
                <Grid item md={3} xs={12}>
                  <CustomMultipleDropDown
                    name="status"
                    label="Status"
                    placeholderText="Select status"
                    options={RX_REQUEST_STATUS_OPTIONS}
                    control={control}
                    size="small"
                  />
                </Grid>
                <Grid item md={3} xs={12}>
                  <CustomDateRange
                    label="Date Range"
                    placeholder="MM/DD/YYYY - MM/DD/YYYY"
                    control={control}
                    controllerName="dateRange"
                    onChange={handleDateRangeChange}
                    onClear={isClearDateRange}
                  />
                </Grid>
                <Grid item md={2} xs={12}>
                  <Button
                    fullWidth
                    size="large"
                    startIcon={<SearchOutlined />}
                    sx={{ ...primaryActionBtnSx, height: "42px", py: 0 }}
                    type="submit"
                    removeUpperCase
                  >
                    Search
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Card>
        </Collapse>
      </Box>

      <Box mt={2} sx={{ position: "relative", zIndex: 1 }}>
        <Card sx={{ ...listCardSx, p: { xs: "14px !important", md: "18px !important" }, position: "relative", zIndex: 1 }}>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            spacing={1.2}
          >
            <Grid item xs={12} md>
              <Typography variant="h3" fontWeight={800} color="text.primary">
                Rx Request List
              </Typography>
            </Grid>
            <Grid item xs={12} md="auto">
              <Button
                variant="outlined"
                color="info"
                onClick={toggleFilterPanel}
                startIcon={<SearchOutlined />}
                fullWidth
                sx={outlineBtnSx}
                removeUpperCase
              >
                {showCard ? "Hide Filters" : "Show Filters"}
              </Button>
            </Grid>
            <Grid item xs={12} md="auto">
              <Button
                color="info"
                onClick={() =>
                  navigate("/pharmacy_coverage", {
                    state: { compoundType: "sterile", pharmacyFilter: "all" } as PharmacyCoverageNavState,
                  })
                }
                startIcon={<MapOutlined />}
                sx={filledTealBtnSx}
                removeUpperCase
              >
                Ship-To Coverage
              </Button>
            </Grid>
            <Grid item xs={12} md="auto">
              <Button
                variant="outlined"
                color="success"
                onClick={() => setShowProductSelectionPopup(true)}
                startIcon={<MedicationOutlinedIcon />}
                fullWidth
                sx={primaryActionBtnSx}
                removeUpperCase
              >
                Request Rx
              </Button>
            </Grid>
          </Grid>
          <Table
            columns={columns}
            rows={prescriptionListData}
            actionComponent={(row: any) => actionComponent(row)}
            emptyText="No Prescriptions Found"
            totalPages={prescriptionListInfo?.totalPages}
            totalElementText={
              "Records: " +
              (prescriptionListInfo?.totalElements
                ? prescriptionListInfo.totalElements
                : "0")
            }
            pageNumber={handlePagination}
            currentPageNumber={searchCriteria.currentPage - 1}
            showMenu={true}
            sx={tableSx}
            onRowClick={(row: any) => canViewPrescription && handlePreviewPDF(row)}
            sortableColumns={prescriptionListSort.sortableColumns}
            serverSideSorting
            onSortChange={handleTableSort}
            controlledSortColumn={prescriptionListSort.controlledColumn(searchCriteria.sortBy)}
            controlledSortOrder={searchCriteria.sortOrder ?? null}
          />
        </Card>
      </Box>
      {showProductSelectionPopup && (
        <RxProductSelectionPopup
          open={showProductSelectionPopup}
          onClose={() => setShowProductSelectionPopup(false)}
          onProductSelect={handleRxProductSelect}
        />
      )}

      {showPrescriptionModal && selectedProductForRx && (
        <PrescriptionModalPopup
          product={selectedProductForRx}
          isOpen={showPrescriptionModal}
          initialPracticeId={initialPracticeIdForRx}
          lockPracticeSelection={initialPracticeIdForRx != null}
          onClose={(closedAfterSuccessfulSubmit) => {
            setShowPrescriptionModal(false);
            setSelectedProductForRx(null);
            setInitialPracticeIdForRx(undefined);
            if (closedAfterSuccessfulSubmit) {
              searchPrescriptionsList(searchCriteria);
            }
          }}
          onSubmit={handleSubmitPrescription}
        />
      )}
    </Box>
  );
};

export default PrescriptionList;